import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Save, Archive, Store, ArrowRightLeft, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HubSchedule } from "@/admin/HubSchedule";
import { HubCommission } from "@/admin/HubCommission";
import { logError } from "@/lib/log";

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";
const TABS = [["profile", "Profil & Setelan"], ["hours", "Jam Operasional"],
  ["commission", "Komisi"], ["units", "Alokasi Unit"], ["partners", "Akses Partner"],
  ["bookings", "Booking & Konflik"], ["audit", "Audit"]];

const err = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (typeof d === "string" ? d : d?.message) || fallback;
};

const Badge = ({ on, children }) => (
  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
    on ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-black/15 text-ink-soft"}`}>
    {children}
  </span>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-ink-soft">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const ProfileTab = ({ detail, onSaved }) => {
  const h = detail.hub;
  const [f, setF] = useState({});
  const [reason, setReason] = useState("");
  useEffect(() => {
    setF({ name: h.name || "", partner_business_name: h.partner_business_name || "",
      address: h.address || "", whatsapp: h.whatsapp || "", maps_url: h.maps_url || "",
      contact: h.contact || "", timezone: h.timezone || "Asia/Jakarta",
      status: h.status || "Active", public_visible: h.public_visible !== false });
  }, [h]);
  const save = async (extra = {}) => {
    try {
      await api.put(`/hubs/${h.hub_id}`, { ...f, ...extra });
      toast.success(`Hub ${f.name} disimpan`);
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.requires === "owner_force_with_reason") toast.error(d.message);
      else toast.error(err(e, "Gagal menyimpan Hub"));
    }
  };
  const archive = async () => {
    try {
      await api.post(`/hubs/${h.hub_id}/archive`, { reason });
      toast.success("Hub diarsipkan");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d?.blockers || []).join(" · ") || err(e, "Gagal mengarsipkan"));
    }
  };
  return (
    <div className="space-y-4" data-testid="hub-tab-profile-body">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nama tampilan"><Input className="h-10" data-testid="hub-profile-name"
          value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Nama partner / bisnis"><Input className="h-10" data-testid="hub-profile-business"
          value={f.partner_business_name || ""} onChange={(e) => setF({ ...f, partner_business_name: e.target.value })} /></Field>
        <div className="sm:col-span-2">
          <Field label="Alamat"><Input className="h-10" data-testid="hub-profile-address"
            value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
        </div>
        <Field label="WhatsApp"><Input className="h-10" data-testid="hub-profile-whatsapp"
          value={f.whatsapp || ""} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} /></Field>
        <Field label="Google Maps URL"><Input className="h-10" data-testid="hub-profile-maps"
          value={f.maps_url || ""} onChange={(e) => setF({ ...f, maps_url: e.target.value })} /></Field>
        <Field label="Timezone"><Input className="h-10" data-testid="hub-profile-timezone"
          value={f.timezone || ""} onChange={(e) => setF({ ...f, timezone: e.target.value })} /></Field>
        <Field label="Status">
          <select className={sel} data-testid="hub-profile-status" value={f.status || "Active"}
            onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="Active">Active</option><option value="Inactive">Inactive</option>
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm self-end">
          <input type="checkbox" data-testid="hub-profile-public" checked={!!f.public_visible}
            onChange={(e) => setF({ ...f, public_visible: e.target.checked })} /> Tampil di website
        </label>
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <button data-testid="hub-profile-save" onClick={() => save()}
          className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
          <Save size={14} /> Simpan profil
        </button>
        <span className="text-xs text-ink-soft">hub_id permanen: <code>{h.hub_id}</code></span>
      </div>

      <div className="mt-6 border-t border-black/10 pt-4">
        <div className="text-[11px] uppercase tracking-widest text-ink-soft">Arsipkan Hub</div>
        <p className="mt-1 text-xs text-ink-soft">
          Hub tidak pernah dihapus. Arsip hanya bisa setelah semua unit dipindah dan booking
          mendatang selesai. Riwayat booking, pembayaran, komisi dan audit tetap utuh.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Input className="h-9 max-w-xs" placeholder="Alasan" data-testid="hub-archive-reason"
            value={reason} onChange={(e) => setReason(e.target.value)} />
          <button data-testid="hub-archive-btn" onClick={archive}
            className="rounded-md border border-black/15 px-4 py-2 text-sm inline-flex items-center gap-2">
            <Archive size={14} /> Arsipkan
          </button>
        </div>
      </div>
    </div>
  );
};

const UnitsTab = ({ detail, hubs, reload }) => {
  const hubId = detail.hub.hub_id;
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState([]);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [form, setForm] = useState({ to_hub_id: "", effective_at: "", reason: "" });

  const load = useCallback(() => {
    api.get(`/hubs/${hubId}/units`, { params: { include_unassigned: showUnassigned } })
      .then((r) => { setRows(r.data); setPicked([]); })
      .catch((e) => logError("Unit Hub gagal dimuat", e));
  }, [hubId, showUnassigned]);
  useEffect(() => { load(); }, [load]);

  const move = async () => {
    try {
      const r = await api.post("/hubs/allocate-units", {
        unit_ids: picked, to_hub_id: form.to_hub_id,
        effective_at: form.effective_at || undefined, reason: form.reason });
      toast.success(r.data.planned
        ? `Transfer terjadwal ${r.data.effective_at.slice(0, 10)} ke ${r.data.to_hub}`
        : `${r.data.moved.length} unit dipindah ke ${r.data.to_hub}`);
      setForm({ to_hub_id: "", effective_at: "", reason: "" });
      load();
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.blocked) {
        toast.error(`${d.message} — ${d.blocked.map((b) => `${b.asset_id}: ${b.conflicts[0]}`).join(" | ")}`);
      } else toast.error(err(e, "Gagal memindahkan unit"));
    }
  };

  return (
    <div data-testid="hub-tab-units-body">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" data-testid="show-unassigned" checked={showUnassigned}
            onChange={(e) => setShowUnassigned(e.target.checked)} /> Tampilkan unit belum teralokasi
        </label>
        <span className="text-xs text-ink-soft">{rows.length} unit · {picked.length} dipilih</span>
      </div>

      <div className="mt-3 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm" data-testid="hub-units-table">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3"></th><th className="p-3">Asset ID</th><th className="p-3">Unit</th>
              <th className="p-3">Status</th><th className="p-3">Bisa dipindah</th>
              <th className="p-3">Konflik</th><th className="p-3">Riwayat</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((u) => (
              <tr key={u.id} data-testid={`unit-row-${u.asset_id}`}>
                <td className="p-3"><input type="checkbox" data-testid={`unit-check-${u.asset_id}`}
                  checked={picked.includes(u.id)}
                  onChange={(e) => setPicked(e.target.checked ? [...picked, u.id] : picked.filter((x) => x !== u.id))} /></td>
                <td className="p-3 font-medium">{u.asset_id}</td>
                <td className="p-3">{u.unit_name || "—"}<div className="text-xs text-ink-soft">{u.model}</div></td>
                <td className="p-3">{u.status}{u.maintenance && <span className="text-lm-red"> · maintenance</span>}</td>
                <td className="p-3">{u.movable_now ? "Ya" : "Tidak"}</td>
                <td className="p-3 text-xs text-ink-soft max-w-xs">
                  {u.conflicts.length ? u.conflicts.join("; ") : "—"}
                  {u.safe_after && <div className="text-lm-red">Aman setelah {u.safe_after.slice(0, 16).replace("T", " ")}</div>}
                </td>
                <td className="p-3 text-xs text-ink-soft">{u.allocation_history.length} perpindahan</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-4 text-ink-soft">Belum ada unit di Hub ini.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-4 bg-white border border-black/10 rounded-lg p-4">
        <div className="text-[11px] uppercase tracking-widest text-ink-soft flex items-center gap-1.5">
          <ArrowRightLeft size={12} /> Pindahkan unit terpilih
        </div>
        <div className="mt-3 grid sm:grid-cols-4 gap-3">
          <select className={sel} data-testid="alloc-target-hub" value={form.to_hub_id}
            onChange={(e) => setForm({ ...form, to_hub_id: e.target.value })}>
            <option value="">Hub tujuan…</option>
            {hubs.filter((x) => x.hub_id !== hubId && x.status === "Active")
              .map((x) => <option key={x.hub_id} value={x.hub_id}>{x.name}</option>)}
          </select>
          <Input type="date" className="h-10" data-testid="alloc-effective" value={form.effective_at}
            onChange={(e) => setForm({ ...form, effective_at: e.target.value })} />
          <Input className="h-10" placeholder="Alasan (wajib)" data-testid="alloc-reason"
            value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <button data-testid="alloc-submit" disabled={!picked.length || !form.to_hub_id || form.reason.length < 3}
            onClick={move}
            className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold disabled:opacity-40">
            Pindahkan {picked.length || ""}
          </button>
        </div>
        <p className="mt-2 text-xs text-ink-soft">
          Asset ID tidak pernah berubah. Unit yang sedang dipakai atau punya booking mendatang tidak
          bisa dipindah langsung — kosongkan tanggal untuk pindah sekarang, atau isi tanggal efektif
          setelah waktu aman agar booking tidak rusak.
        </p>
      </div>
    </div>
  );
};

export default function HubManagement() {
  const [hubs, setHubs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [query, setQuery] = useState({ q: "", status: "" });
  const [openId, setOpenId] = useState("");
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("profile");
  const [adding, setAdding] = useState(false);
  const [nf, setNf] = useState({ name: "", code: "", address: "", whatsapp: "",
    partner_business_name: "", timezone: "Asia/Jakarta", play_at_venue_enabled: false });

  const load = useCallback(() => {
    api.get("/hubs", { params: { q: query.q || undefined, status: query.status || undefined } })
      .then((r) => setHubs(r.data)).catch((e) => logError("Hub gagal dimuat", e));
    api.get("/public/packages").then((r) => setPackages(r.data)).catch(() => setPackages([]));
  }, [query]);
  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback((id) => {
    api.get(`/hubs/${id}`).then((r) => setDetail(r.data)).catch((e) => logError("Detail Hub gagal", e));
  }, []);
  useEffect(() => { if (openId) loadDetail(openId); else setDetail(null); }, [openId, loadDetail]);

  const create = async () => {
    try {
      const r = await api.post("/hubs", nf);
      toast.success(`Hub ${r.data.name} dibuat — Main di Venue masih OFF sampai Anda nyalakan`);
      setAdding(false);
      setNf({ name: "", code: "", address: "", whatsapp: "", partner_business_name: "",
        timezone: "Asia/Jakarta", play_at_venue_enabled: false });
      load();
      setOpenId(r.data.hub_id);
    } catch (e) { toast.error(err(e, "Gagal membuat Hub")); }
  };

  const refreshAll = () => { load(); if (openId) loadDetail(openId); };
  const venueHubs = useMemo(() => hubs.filter((h) => h.play_at_venue_enabled).length, [hubs]);

  return (
    <div data-testid="hub-management">
      <h1 className="font-display text-3xl tracking-tight">Hub Management</h1>
      <p className="mt-1 text-sm text-ink-soft">
        Tambah Hub baru kapan saja tanpa deploy. Setiap Hub punya jam operasional, layanan, unit dan
        staf partner sendiri. {venueHubs} dari {hubs.length} Hub melayani Main di Venue.
      </p>

      <div className="mt-5 flex flex-wrap gap-3">
        <Input className="h-10 max-w-xs" placeholder="Cari nama / kode / alamat" data-testid="hub-search"
          value={query.q} onChange={(e) => setQuery({ ...query, q: e.target.value })} />
        <select className="h-10 rounded-md border border-input bg-white px-3 text-sm"
          data-testid="hub-filter-status" value={query.status}
          onChange={(e) => setQuery({ ...query, status: e.target.value })}>
          <option value="">Semua status</option><option value="Active">Active</option>
          <option value="Inactive">Inactive</option><option value="Archived">Archived</option>
        </select>
        <button data-testid="hub-add-btn" onClick={() => setAdding(true)}
          className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
          <Plus size={15} /> Tambah Hub
        </button>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {hubs.map((h) => (
          <button key={h.hub_id} data-testid={`hub-card-${h.code}`}
            onClick={() => { setOpenId(h.hub_id === openId ? "" : h.hub_id); setTab("profile"); }}
            className={`text-left bg-white border rounded-lg p-4 transition-colors ${
              openId === h.hub_id ? "border-lm-red ring-1 ring-lm-red" : "border-black/10 hover:border-ink/30"}`}>
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-lm-red" />
              <span className="font-semibold">{h.name}</span>
              <span className="text-xs text-ink-soft">{h.code}</span>
              <span className="ml-auto"><Badge on={h.status === "Active"}>{h.status}</Badge></span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge on={h.play_at_venue_enabled}>
                {h.play_at_venue_enabled ? "Main di Venue ON" : "Main di Venue OFF"}
              </Badge>
              <Badge on={h.units_total > 0}>{h.units_ready}/{h.units_total} unit siap</Badge>
              <Badge on={h.partners_count > 0}>{h.partners_count} partner</Badge>
            </div>
            <div className="mt-2 text-xs text-ink-soft">
              Hari ini {h.today_hours} · {h.future_bookings} booking mendatang
            </div>
          </button>
        ))}
        {hubs.length === 0 && <p className="text-sm text-ink-soft">Tidak ada Hub yang cocok.</p>}
      </div>

      {detail && (
        <div className="mt-6 bg-cream-dark/40 border border-black/10 rounded-xl p-4" data-testid="hub-detail">
          <div className="flex items-center gap-2 flex-wrap">
            <Store size={16} className="text-lm-red" />
            <span className="font-semibold" data-testid="hub-detail-name">{detail.hub.name}</span>
            <Badge on={detail.hub.play_at_venue_enabled}>
              {detail.hub.play_at_venue_enabled ? "Main di Venue ON" : "Main di Venue OFF"}
            </Badge>
            {detail.conflicts.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-lm-red" data-testid="hub-conflict-warning">
                <AlertTriangle size={13} /> {detail.conflicts.length} booking berkonflik
              </span>
            )}
          </div>

          <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1">
            {TABS.map(([k, label]) => (
              <button key={k} data-testid={`hub-tab-${k}`} onClick={() => setTab(k)}
                className={`whitespace-nowrap px-3.5 py-2 rounded-full text-xs border ${
                  tab === k ? "bg-ink text-cream border-ink" : "border-black/15"}`}>{label}</button>
            ))}
          </div>

          <div className="mt-4">
            {tab === "profile" && <ProfileTab detail={detail} onSaved={refreshAll} />}
            {tab === "hours" && (
              <HubSchedule branch={{ ...detail.hub, id: detail.hub.hub_id }} packages={packages}
                onSaved={refreshAll} />
            )}
            {tab === "commission" && <HubCommission hubId={detail.hub.hub_id} />}
            {tab === "units" && <UnitsTab detail={detail} hubs={hubs} reload={refreshAll} />}
            {tab === "partners" && (
              <div data-testid="hub-tab-partners-body" className="bg-white border border-black/10 rounded-lg p-4">
                {detail.partners.length === 0 && (
                  <p className="text-sm text-ink-soft">Belum ada staf partner di Hub ini. Tambahkan
                    dari halaman Staff Accounts (role <b>partner</b>) lalu pilih Hub ini.</p>
                )}
                <ul className="space-y-2">
                  {detail.partners.map((p) => (
                    <li key={p.user_id} className="text-sm" data-testid={`hub-partner-${p.email}`}>
                      <span className="font-medium">{p.name || p.email}</span>
                      <span className="text-ink-soft"> · {p.email} · {p.hub_branch_ids.length} Hub
                        {p.commission_percent != null ? ` · komisi ${p.commission_percent}%` : ""}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {tab === "bookings" && (
              <div data-testid="hub-tab-bookings-body" className="overflow-x-auto bg-white border border-black/10 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
                    <tr><th className="p-3">Kode</th><th className="p-3">Customer</th><th className="p-3">Jadwal</th>
                      <th className="p-3">Layanan</th><th className="p-3">Status</th><th className="p-3">Konflik</th></tr>
                  </thead>
                  <tbody className="divide-y divide-black/5">
                    {detail.future_bookings.map((b) => {
                      const c = detail.conflicts.find((x) => x.booking_code === b.booking_code);
                      return (
                        <tr key={b.booking_code} data-testid={`hub-booking-${b.booking_code}`}>
                          <td className="p-3 font-medium">{b.booking_code}</td>
                          <td className="p-3">{b.customer_name}</td>
                          <td className="p-3">{b.rental_date} {b.start_time}</td>
                          <td className="p-3 text-xs">{b.service_mode}</td>
                          <td className="p-3 text-xs">{b.payment_status} / {b.booking_status}</td>
                          <td className="p-3 text-xs text-lm-red">{c?.conflict || "—"}</td>
                        </tr>
                      );
                    })}
                    {detail.future_bookings.length === 0 && (
                      <tr><td colSpan={6} className="p-4 text-ink-soft">Tidak ada booking mendatang.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {tab === "audit" && (
              <div data-testid="hub-tab-audit-body" className="space-y-4">
                <div className="overflow-x-auto bg-white border border-black/10 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
                      <tr><th className="p-3">Waktu</th><th className="p-3">Aksi</th><th className="p-3">Oleh</th><th className="p-3">Alasan</th></tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                      {detail.audit.map((a) => (
                        <tr key={a.id}>
                          <td className="p-3 text-xs">{a.at_jkt}</td>
                          <td className="p-3">{a.action}</td>
                          <td className="p-3 text-xs">{a.actor_email} ({a.actor_role})</td>
                          <td className="p-3 text-xs text-ink-soft">{a.reason || "—"}</td>
                        </tr>
                      ))}
                      {detail.audit.length === 0 && <tr><td colSpan={4} className="p-4 text-ink-soft">Belum ada perubahan.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="overflow-x-auto bg-white border border-black/10 rounded-lg">
                  <table className="w-full text-sm" data-testid="hub-transfers-table">
                    <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
                      <tr><th className="p-3">Asset ID</th><th className="p-3">Dari</th><th className="p-3">Ke</th>
                        <th className="p-3">Tanggal</th><th className="p-3">Status</th><th className="p-3">Alasan</th></tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                      {detail.transfers.map((t) => (
                        <tr key={t.id}>
                          <td className="p-3 font-medium">{t.asset_id}</td>
                          <td className="p-3">{t.from_branch_name || "—"}</td>
                          <td className="p-3">{t.to_branch_name}</td>
                          <td className="p-3">{t.transfer_date}</td>
                          <td className="p-3">{t.status}</td>
                          <td className="p-3 text-xs text-ink-soft">{t.notes}</td>
                        </tr>
                      ))}
                      {detail.transfers.length === 0 && <tr><td colSpan={6} className="p-4 text-ink-soft">Belum ada perpindahan unit.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Tambah Hub</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Nama Hub"><Input className="h-10" data-testid="hub-new-name" value={nf.name}
              onChange={(e) => setNf({ ...nf, name: e.target.value })} /></Field>
            <Field label="Kode (kosongkan untuk otomatis)"><Input className="h-10" data-testid="hub-new-code"
              value={nf.code} onChange={(e) => setNf({ ...nf, code: e.target.value })} /></Field>
            <Field label="Nama partner / bisnis"><Input className="h-10" data-testid="hub-new-business"
              value={nf.partner_business_name} onChange={(e) => setNf({ ...nf, partner_business_name: e.target.value })} /></Field>
            <Field label="Alamat"><Input className="h-10" data-testid="hub-new-address" value={nf.address}
              onChange={(e) => setNf({ ...nf, address: e.target.value })} /></Field>
            <Field label="WhatsApp"><Input className="h-10" data-testid="hub-new-whatsapp" value={nf.whatsapp}
              onChange={(e) => setNf({ ...nf, whatsapp: e.target.value })} /></Field>
            <Field label="Timezone"><Input className="h-10" data-testid="hub-new-timezone" value={nf.timezone}
              onChange={(e) => setNf({ ...nf, timezone: e.target.value })} /></Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="hub-new-venue" checked={nf.play_at_venue_enabled}
                onChange={(e) => setNf({ ...nf, play_at_venue_enabled: e.target.checked })} />
              Hub ini punya tempat bermain (Main di Venue)
            </label>
            <p className="text-xs text-ink-soft">
              Default OFF. Hub yang hanya jadi titik simpan/ambil/kirim tidak perlu Main di Venue.
            </p>
            <button data-testid="hub-create-submit" disabled={!nf.name.trim()} onClick={create}
              className="w-full lm-btn-primary rounded-md py-2.5 text-sm font-semibold disabled:opacity-40">
              Buat Hub
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
